import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import usePollingSearchAsyncOrchestrator from '@webclient/components/pages/hooks/usePollingSearchAsyncOrchestrator';
import FormProvider from '@webclient/components/react-hook-form/FormProvider';
import searchFormSchema from '@webclient/components/search-forms/gamification-search/components/search-box-react-hook-form/schema';
import TextareaIconButton from '@webclient/components/search-forms/gamification-search/components/search-box-react-hook-form/textarea-icon-button';
import { SearchBoxValuesType } from '@webclient/components/search-forms/gamification-search/types';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';

import { selectBot } from '@webclient/state/slices/app-settings/userSettingSelector';
import {
  startVoiceChatMode,
  updateVoiceModChatStatus,
  voiceModChat,
} from '@webclient/state/slices/search-session';
import {
  resetTempTranscript,
  resetTranscript,
  setTranscript,
  stopDictation,
} from '@webclient/state/slices/search-widget-dictation';

import { yupResolver } from '@hookform/resolvers/yup';
import { SearchWidget } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import paths from '@webclient/router/paths';
import { Controller, useForm } from 'react-hook-form';

type Props = {
  origin: 'search' | 'home';
  Slot: typeof SearchWidget.SearchBox;
};

const PLACEHOLDERS = {
  default: {
    translationKey: 'page.home.hero.search.placehoolders.default',
    fallback: 'Ask Anything...',
  },
  searching: {
    translationKey: 'page.home.hero.search.placehoolders.searching',
    fallback: 'Searching...',
  },
  attachment: {
    translationKey: 'page.home.hero.search.placehoolders.attachment',
    fallback: 'Ask about attachments...',
  },
  upload: {
    translationKey: 'page.home.hero.search.placehoolders.upload',
    fallback: 'Uploading...',
  },
  processing: {
    translationKey: 'page.home.hero.search.placehoolders.processing',
    fallback: 'Processing...',
  },
};

export default function SearchBoxReactHookForm({ Slot, origin }: Props) {
  const [isAleadySubmitted, setIsAlreadySubmitted] = useState(false);
  const { id: threadId } = useParams<{ id: string }>();
  const attachments = useAppSelector(
    (state) => state.searchSession.attachments,
  );
  const areAttachmentsPending = useMemo(
    () => attachments.some((attachment) => attachment.isPending),
    [attachments],
  );
  const areAttachmentsProcessing = useMemo(
    () => attachments.some((attachment) => attachment.isProcessingByAI),
    [attachments],
  );
  const isRealtimeOne = useAppSelector(
    (state) => state.searchSession.voiceChatMode.mode,
  );
  const isDictationOn = useAppSelector(
    (state) => state.searchWidgetDictation.isDictationOn,
  );
  const tempTranscript = useAppSelector(
    (state) => state.searchWidgetDictation.tempTranscript,
  );
  const transcript = useAppSelector(
    (state) => state.searchWidgetDictation.transcript,
  );
  const bot = useAppSelector(selectBot);
  const methods = useForm({
    resolver: yupResolver(searchFormSchema),
    mode: 'onChange',
    defaultValues: {
      prompt: '',
    },
  });
  const isReceivingMessages = useAppSelector(
    (state) => state.searchSession.isReceivingMessages,
  );
  const isUserAuthenticated = useAppSelector(
    (state) => state.auth.isUserAuthenticated,
  );
  const { t } = useTranslate();

  const { onSubmit, createSearchAndConnectToRoom, polling } =
    usePollingSearchAsyncOrchestrator(
      origin === 'search' && threadId
        ? {
            requestOrigin: 'search',
            searchThreadId: threadId,
          }
        : {
            requestOrigin: 'home',
          },
    );
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    control,
    setValue,
    setFocus,
    resetField,
    formState: { isSubmitting, errors },
  } = methods;
  const shouldDisable =
    !isUserAuthenticated || isSubmitting || isDictationOn || polling;

  const shouldDisableFormSubmission =
    polling ||
    isReceivingMessages ||
    areAttachmentsPending ||
    areAttachmentsProcessing;

  const handleSubmitSearch = useCallback(
    async (data: SearchBoxValuesType) => {
      if (shouldDisableFormSubmission) return;
      onSubmit(data);
      dispatch(resetTempTranscript());
      dispatch(resetTranscript());
      if (isDictationOn) {
        dispatch(stopDictation());
      }
      resetField('prompt');
    },
    [
      dispatch,
      isDictationOn,
      onSubmit,
      resetField,
      shouldDisableFormSubmission,
    ],
  );

  useEffect(() => {
    if (!isDictationOn || !transcript) return;
    if (errors.prompt?.type === 'max' && !isRealtimeOne) {
      dispatch(stopDictation());
      dispatch(resetTempTranscript());
      dispatch(resetTranscript());
      return;
    }
    setFocus('prompt');
    setValue('prompt', `${transcript} ${tempTranscript ?? ''}`, {
      shouldValidate: true,
    });
  }, [
    dispatch,
    errors.prompt?.type,
    isDictationOn,
    isRealtimeOne,
    setFocus,
    setValue,
    tempTranscript,
    transcript,
  ]);

  async function handleVoiceMode() {
    dispatch(updateVoiceModChatStatus('loading'));
    const { payload, meta } = await createSearchAndConnectToRoom({
      withAudio: true,
    });
    dispatch(startVoiceChatMode());

    dispatch(voiceModChat({ realtime: true, bot }));

    if (meta.arg.requestOrigin === 'home') {
      navigate(
        paths.search.pathname.replace(
          ':id',
          (payload as { threadId: string }).threadId,
        ),
      );
      return;
    }

    dispatch(updateVoiceModChatStatus(null));
  }

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(handleSubmitSearch)}
    >
      <Controller
        control={control}
        name="prompt"
        render={({
          field: { ref, onChange, ...otherFields },
          formState: { errors },
        }) => {
          const isItRequiredPromptMessage =
            errors.prompt?.type === 'required' && !errors.root?.message;

          function handleSearchboxOnChange(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) {
            dispatch(resetTranscript());
            dispatch(setTranscript(event.currentTarget.value));
            onChange(event);
            if (!isAleadySubmitted) {
              createSearchAndConnectToRoom({});
              setIsAlreadySubmitted(true);
            }
            //? submit once and only one time when user starts typing
          }

          const defaultPlaceholder =
            isReceivingMessages && PLACEHOLDERS.searching;

          const attachmentPlaceholder =
            attachments.length > 0 &&
            !areAttachmentsPending &&
            PLACEHOLDERS.attachment;

          const uploadingPlaceholder =
            attachments.length > 0 &&
            areAttachmentsPending &&
            PLACEHOLDERS.upload;

          const processingPlaceholder =
            attachments.length > 0 &&
            areAttachmentsProcessing &&
            !areAttachmentsPending &&
            PLACEHOLDERS.processing;

          const placeholder =
            uploadingPlaceholder ||
            processingPlaceholder ||
            attachmentPlaceholder ||
            defaultPlaceholder ||
            PLACEHOLDERS.default;

          return (
            <Slot
              textareaProps={{
                placeholder: t(
                  placeholder.translationKey,
                  placeholder.fallback,
                ),
                error: !!errors.prompt?.message,
                helperText: !isItRequiredPromptMessage
                  ? errors.prompt?.message
                  : undefined,
                minRows: 1,
                disabled: shouldDisable,
                maxRows: 4,
                fullWidth: true,
                readOnly: isDictationOn,
                title: t(placeholder.translationKey, placeholder.fallback),
                inputRef: ref,
                onChange: handleSearchboxOnChange,
                inputProps: {
                  sx: {
                    paddingInlineEnd: 4.25,
                  },
                },
                ...otherFields,
                endAdornment: (
                  <TextareaIconButton
                    variant="ghost"
                    size="small"
                    shape="squareRounded"
                    disabled={shouldDisableFormSubmission}
                    color="secondary"
                    onSendMessage={handleSubmit(handleSubmitSearch)}
                    onVoiceMode={handleVoiceMode}
                    onChange={onChange}
                    value={otherFields.value}
                  />
                ),
              }}
            />
          );
        }}
      />
    </FormProvider>
  );
}
